import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { IBrand, IClient } from './types/brand';
import { IAgent, IAgentBrand } from './types/agent';
import { permissionRouteMap } from './constants';
import { Payment } from './types/agent';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatTime = (time: number): string => {
  // Converts a time in seconds to a formatted string "HH:MM:SS"
  const hours = Math.floor(time / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((time % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.round(time % 60)
    .toString()
    .padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};
export const formatTimeInHms = (time: number): string => {
  // Converts a time in seconds to a formatted string "h m s"
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.round(time % 60);

  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0) {
    result += `${minutes}m `;
  }
  // Show seconds if there are any, or if total time is less than a minute
  if (seconds > 0 || result === '') {
    result += `${seconds}s`;
  }

  return result.trim();
};

export const formatTimeHandler = (date: string) => {
  return momentTz.tz(moment(date), momentTz.tz.guess());
};

export const formatWorkTime = (duration: number) => {
  const hours = Math.floor(duration / 60);
  const minutes = Math.floor(duration % 60);
  return { hours, minutes };
};

export const calculateAge = (dob: string) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age < 18 ? 'Minor' : 'Major';
};

export const generatePassword = () => {
  const length = 12;
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

export const formatDate = (date: string) => {
  if (!date) return '-';
  return moment(date).format('DD/MM/YYYY');
};

export const formatToYMD = (date: Date | string) => {
  if (!date) return '';
  return moment(date).format('YYYY-MM-DD');
};

export const parseDate = (str: string) => {
  const [day, month, year] = str.split('/');
  return new Date(`${year}-${month}-${day}`);
};

export const formatClientData = (brands: IBrand[]) => {
  if (!brands) return [];
  const clientMap = new Map<string, { client: IClient; brands: IBrand[] }>();

  brands.forEach((brand) => {
    const client = brand.clientId as IClient;
    if (!client || !client._id) return;

    if (!clientMap.has(client._id)) {
      clientMap.set(client._id, { client, brands: [] });
    }
    clientMap.get(client._id)!.brands.push(brand);
  });
  return Array.from(clientMap.values());
};

export const getClientBrands = (data: any, clientId: string) => {
  if (!data || !clientId) return [];

  return data.find((item: any) => item.client._id === clientId)?.brands;
};

export const calculateWorkingHours = (
  startTime: string,
  endTime: string
): number => {
  if (!startTime || !endTime) return 0;

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  let endMinutes = endHour * 60 + endMin;

  // Handle overnight shift
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }

  const diffMinutes = endMinutes - startMinutes;
  return Math.max(0, diffMinutes / 60);
};

export const getClientBrandAgents = (
  brands: IBrand[],
  agents: IAgent[]
): IAgent[] => {
  const result: IAgent[] = [];

  for (const brand of brands) {
    const brandAgentObjs = agents.filter((agent) =>
      brand.agents?.includes(agent.kimId!)
    );
    result.push(...brandAgentObjs);
  }

  return result;
};

export const validateUrl = (url: string) => {
  return /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)$/.test(
    url
  );
};

export const getFirstLetter = (name: string) => {
  if (!name || name.length === 0) return '';
  return name?.charAt(0).toUpperCase();
};

export const getFilteredAgentBrands = (
  agentBrands: IAgentBrand[],
  initialData: IAgent
) => {
  return agentBrands
    .filter(
      (ab) =>
        ab.agentId === initialData?._id &&
        ab.brandId?.agents?.includes(initialData.kimId!)
    )
    .map((ab: IAgentBrand) => {
      const brandId =
        typeof ab.brandId === 'object' ? ab.brandId?._id : ab.brandId;

      return {
        ...ab,
        brandId,
      };
    });
};

export const handleClickOutside = (
  refElement: React.RefObject<HTMLDivElement>,
  setIsOpen: (isOpen: boolean) => void
) => {
  const handleClickOutside = (e: MouseEvent) => {
    if (refElement && !refElement.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
};

export const getTodayDate = () => {
  return moment().format('YYYY-MM-DD');
};

export const getCurrentDateTime = () => {
  return moment().toISOString();
};

export const getBillDate = () => {
  return moment().date(10).format('YYYY-MM-DD');
};

export const getCurrentMonthFirstDate = () => {
  return moment().startOf('month').format('YYYY-MM-DD');
};

export const getCurrentMonthLastDate = () => {
  return moment().endOf('month').format('YYYY-MM-DD');
};

export const getBrandsForClient = (brands: IBrand[], clientId: string) => {
  return brands.filter((b) => b.clientId?._id === clientId);
};

export const filterAgentsForBrands = (agents: IAgent[], brands: IBrand[]) => {
  return agents.filter((a) => brands.some((b) => b.agents?.includes(a.kimId!)));
};

export const getAgentPay = (
  agentBrands: IAgentBrand[],
  id: string,
  field: keyof IAgentBrand,
  from: 'toAgent' | 'fromClient'
) => {
  let payment;
  if (field === 'agentId') {
    payment = agentBrands.find((ab) => ab.agentId === id)?.payment;
  } else {
    payment = agentBrands.find((ab) => ab.brandId?._id === id)?.payment;
  }

  const activePayment = getActivePayment(payment);
  return activePayment?.[from]?.amount || 0;
};

export const getFirstAccessibleRoute = (permissions: string[]): string => {
  try {
    for (const key of permissions) {
      if (permissionRouteMap[key as keyof typeof permissionRouteMap]) {
        return permissionRouteMap[key as keyof typeof permissionRouteMap];
      }
    }
  } catch (e) {
    console.error('Failed to get first accessible route', e);
  }

  return '/login';
};

export const safeLink = (link: string) => {
  return link.startsWith('http') ? link : `https://${link}`;
};

export const getChangedFields = (data: any, initial: any) => {
  const changed: any = {};
  Object.keys(data).forEach((key) => {
    const newVal = data[key];
    const oldVal = initial?.[key];

    // deep compare for objects/arrays, shallow for primitives
    const isEqual = JSON.stringify(newVal) === JSON.stringify(oldVal);

    if (!isEqual) {
      changed[key] = newVal;
    }
  });
  return changed;
};

export const downloadCSV = (csvContent: string, fileName: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const getActivePayment = (payment: Payment | undefined) => {
  if (!payment) return undefined;

  // If payment is an array, find the active record (no effectiveEndDate)
  if (Array.isArray(payment)) {
    const activePayment = payment.find((p) => !p.effectiveEndDate);
    return activePayment || payment[payment.length - 1];
  }

  // Old format: payment is an object
  return payment;
};
